import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon, XMarkIcon, CameraIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMutation, useMasterData } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatMaster, GoatCategory, GoatStatus } from '../../../types/api';
import { compressImage, validateImageFile } from '../../../utils/imageCompression';

interface GoatMasterFormData {
  tag_no: string;
  category: GoatCategory;
  breed?: string;
  purchase_date?: string;
  purchase_amount?: number;
  age_months?: number;
  dob?: string;
  shed_id?: string;
  mother_tag?: string;
  father_tag?: string;
  status: GoatStatus;
  is_breeder?: boolean;
  // Sale information (conditional)
  sale_date?: string;
  sale_amount?: number;
  // Death information (conditional)
  dead_date?: string;
  reason_of_death?: string;
  // Latest weight
  latest_weight_kg?: number;
  latest_weight_date?: string;
}

// Base schema - will be extended with dynamic validation
const createGoatMasterSchema = (existingGoats: GoatMaster[] = [], currentGoatId?: string) => yup.object({
  tag_no: yup.string()
    .required('Tag number is required')
    .test('unique-tag', 'This tag number already exists (case-insensitive)', function(value) {
      if (!value) return true; // Let required validation handle empty values
      const tagLower = value.toLowerCase();
      const duplicate = existingGoats.find(g => 
        g.tag_no.toLowerCase() === tagLower && g.id !== currentGoatId
      );
      return !duplicate;
    }),
  category: yup.string().oneOf(['doe', 'buck', 'kid']).required('Category is required'),
  breed: yup.string().optional(),
  purchase_date: currentGoatId 
    ? yup.string().optional() // Optional in edit mode
    : yup.string()
        .required('Purchase date is required')
        .test('not-empty', 'Purchase date is required', function(value) {
          return value !== '' && value !== null && value !== undefined;
        }),
  age_months: currentGoatId
    ? yup.number()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === '' || originalValue === null || originalValue === undefined ? null : Number(originalValue);
        })
        .test('min-value', 'Age must be 0 or greater', function(value) {
          return value === null || value === undefined || value >= 0;
        })
        .test('max-value', 'Age must be reasonable (less than 300 months / 25 years)', function(value) {
          return value === null || value === undefined || value < 300;
        })
    : yup.number()
        .required('Estimated age at purchase is required')
        .transform((value, originalValue) => {
          if (originalValue === '' || originalValue === null || originalValue === undefined) {
            return undefined; // Return undefined to trigger required validation
          }
          const num = Number(originalValue);
          return isNaN(num) ? undefined : num;
        })
        .test('required', 'Estimated age at purchase is required', function(value) {
          return value !== null && value !== undefined;
        })
        .test('min-value', 'Age must be 0 or greater', function(value) {
          return value !== null && value !== undefined && value >= 0;
        })
        .test('max-value', 'Age must be reasonable (less than 300 months / 25 years)', function(value) {
          return value !== null && value !== undefined && value < 300;
        }),
  dob: yup.string().optional(),
  shed_id: yup.string().optional(),
  mother_tag: yup.string().optional(),
  father_tag: yup.string().optional(),
  status: yup.string().oneOf(['active', 'sold', 'dead', 'transferred']).required('Status is required'),
  sale_date: yup.string().optional(),
  purchase_amount: currentGoatId
    ? yup.number().nullable().transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))) {
          return null;
        }
        const numValue = Number(originalValue);
        return isNaN(numValue) ? null : numValue;
      }).test('min-value', 'Purchase amount must be 0 or greater', function(value) {
        return value === null || value === undefined || value >= 0;
      })
    : yup.number()
        .required('Purchase amount is required')
        .transform((value, originalValue) => {
          if (originalValue === '' || originalValue === null || originalValue === undefined) {
            return undefined; // Return undefined to trigger required validation
          }
          const num = Number(originalValue);
          return isNaN(num) ? undefined : num;
        })
        .test('required', 'Purchase amount is required', function(value) {
          return value !== null && value !== undefined;
        })
        .test('min-value', 'Purchase amount must be greater than 0', function(value) {
          return value !== null && value !== undefined && value > 0;
        }),
  sale_amount: yup.number().nullable().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))) {
      return null;
    }
    const numValue = Number(originalValue);
    return isNaN(numValue) ? null : numValue;
  }).test('min-value', 'Sale amount must be 0 or greater', function(value) {
    return value === null || value === undefined || value >= 0;
  }),
  dead_date: yup.string().optional(),
  reason_of_death: yup.string().optional(),
  latest_weight_kg: yup.number().nullable().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))) {
      return null;
    }
    const numValue = Number(originalValue);
    return isNaN(numValue) ? null : numValue;
  }).test('min-value', 'Weight must be 0 or greater', function(value) {
    return value === null || value === undefined || value >= 0;
  }),
  latest_weight_date: yup.string().optional(),
}).test('purchase-date-if-age', 'Purchase date is required when age at purchase is provided', function(value) {
  // Only apply this validation in edit mode
  if (currentGoatId) {
    const { age_months, purchase_date } = value;
    // If age_months is provided (not null/undefined), purchase_date must also be provided
    if (age_months !== null && age_months !== undefined) {
      return !!purchase_date;
    }
  }
  return true;
});

const GoatMasterForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState('');
  const [existingGoats, setExistingGoats] = useState<GoatMaster[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const { data: goatBreeds } = useMasterData('goat_breeds', selectedLocation?.id || null);
  // Image state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);

  // Redirect non-admin users away from edit page
  useEffect(() => {
    if (id && user?.role !== 'admin') {
      navigate('/goat/master');
    }
  }, [id, user?.role, navigate]);

  const { mutate: createGoat, loading: creating } = useMutation<GoatMaster, GoatMasterFormData>();
  const { mutate: updateGoat, loading: updating } = useMutation<GoatMaster, GoatMasterFormData>();

  // Create schema dynamically with access to existingGoats
  const goatMasterSchema = useMemo(() => 
    createGoatMasterSchema(existingGoats, id || undefined),
    [existingGoats, id]
  );

  // Get today's date in YYYY-MM-DD format for default purchase_date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const form = useForm<GoatMasterFormData>({
    resolver: yupResolver(goatMasterSchema) as any,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      tag_no: '',
      category: 'doe',
      breed: '',
      purchase_date: '',
      purchase_amount: undefined,
      age_months: undefined,
      dob: '',
      shed_id: '',
      mother_tag: '',
      father_tag: '',
      status: 'active',
      is_breeder: false,
      sale_date: '',
      sale_amount: undefined,
      dead_date: '',
      reason_of_death: '',
      latest_weight_kg: undefined,
      latest_weight_date: '',
    },
  });

  useEffect(() => {
    if (businessType !== 'goat' || !selectedLocation?.id) return;

    // Load existing goats for parent tag validation
    loadExistingGoats();
    // Load sheds
    loadSheds();

    // Load goat data if editing
    if (id) {
      loadGoatData();
    }
  }, [id, selectedLocation?.id, businessType]);

  // Re-validate tag_no field when existingGoats changes
  useEffect(() => {
    if (existingGoats.length > 0 && form.watch('tag_no')) {
      form.trigger('tag_no');
    }
  }, [existingGoats]);

  // Reset is_breeder to false when category changes from buck to something else
  const watchedCategory = form.watch('category');
  useEffect(() => {
    if (watchedCategory !== 'buck' && form.getValues('is_breeder')) {
      form.setValue('is_breeder', false);
    }
  }, [watchedCategory]);

  const loadExistingGoats = async () => {
    try {
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}`);
      setExistingGoats(response.data || []);
    } catch (err) {
      console.error('Failed to load existing goats:', err);
    }
  };

  const loadSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err) {
      console.error('Failed to load sheds:', err);
    }
  };

  const loadGoatData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/goat/master/${id}`);
      const goat = response.data;
      
      console.log('Loaded goat data:', goat);
      console.log('age_months value:', goat.age_months, 'type:', typeof goat.age_months);
      
      // Use purchase_date if available, otherwise leave empty
      const purchaseDate = goat.purchase_date 
        ? goat.purchase_date.split('T')[0] 
        : '';
      
      // Use age_months if available (including 0), otherwise use undefined
      // Check explicitly for null/undefined, not falsy values (since 0 is valid)
      const ageMonths = (goat.age_months !== null && goat.age_months !== undefined && goat.age_months !== '')
        ? Number(goat.age_months) 
        : undefined;
      
      console.log('Formatted ageMonths:', ageMonths, 'type:', typeof ageMonths);
      
      form.reset({
        tag_no: goat.tag_no,
        category: goat.category,
        breed: goat.breed || '',
        purchase_date: purchaseDate,
        purchase_amount: goat.purchase_amount !== null && goat.purchase_amount !== undefined ? goat.purchase_amount : undefined,
        age_months: ageMonths,
        dob: goat.dob ? goat.dob.split('T')[0] : '',
        shed_id: goat.shed_id || '',
        mother_tag: goat.mother_tag || '',
        father_tag: goat.father_tag || '',
        status: goat.status,
        is_breeder: goat.is_breeder || false,
        sale_date: goat.sale_date ? goat.sale_date.split('T')[0] : '',
        sale_amount: goat.sale_amount !== null && goat.sale_amount !== undefined ? goat.sale_amount : undefined,
        dead_date: goat.dead_date ? goat.dead_date.split('T')[0] : '',
        reason_of_death: goat.reason_of_death || '',
        latest_weight_kg: goat.latest_weight_kg !== null && goat.latest_weight_kg !== undefined ? goat.latest_weight_kg : undefined,
        latest_weight_date: goat.latest_weight_date ? goat.latest_weight_date.split('T')[0] : '',
      });

      // Load existing image on-demand
      await loadGoatImage(id!);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load goat data'));
    } finally {
      setLoading(false);
    }
  };

  const loadGoatImage = async (goatId: string) => {
    try {
      const response = await apiClient.get(`/goat/master/${goatId}/image`);
      setExistingImage(response.data.image);
    } catch (err: any) {
      // Image doesn't exist, that's okay
      if (err.response?.status !== 404) {
        console.error('Failed to load goat image:', err);
      }
      setExistingImage(null);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    try {
      setError('');
      // Compress image
      const compressedBase64 = await compressImage(file);
      setImagePreview(compressedBase64);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to process image'));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('goat-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const removeExistingImage = async () => {
    if (!id) return;
    try {
      setUploadingImage(true);
      await apiClient.delete(`/goat/master/${id}/image`);
      setExistingImage(null);
      setError('');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to remove image'));
    } finally {
      setUploadingImage(false);
    }
  };

  const openImageModal = (imageSrc: string) => {
    setModalImageSrc(imageSrc);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImageSrc(null);
  };

  const uploadGoatImage = async (goatId: string, imageBase64: string) => {
    try {
      await apiClient.post(`/goat/master/${goatId}/image`, {
        goat_id: goatId,
        image: imageBase64,
      });
    } catch (err: any) {
      throw new Error(getErrorMessage(err, 'Failed to upload image'));
    }
  };

  const handleSubmit = async (data: GoatMasterFormData) => {
    try {
      setError('');
      console.log('Form submit data:', data);
      console.log('age_months in submit:', data.age_months, 'type:', typeof data.age_months);
      const goatData: any = {
        ...data,
        location_id: selectedLocation?.id,
        // Ensure empty strings are converted to undefined for optional fields
        breed: data.breed === '' || data.breed === null ? undefined : data.breed,
        mother_tag: data.mother_tag === '' || data.mother_tag === null ? undefined : data.mother_tag,
        father_tag: data.father_tag === '' || data.father_tag === null ? undefined : data.father_tag,
        shed_id: data.shed_id === '' || data.shed_id === null ? undefined : data.shed_id,
        // Purchase date and age_months (required in add mode, optional in edit mode)
        purchase_date: data.purchase_date === '' || data.purchase_date === null ? undefined : data.purchase_date,
        purchase_amount: (data.purchase_amount === null || data.purchase_amount === undefined || data.purchase_amount === 0 || isNaN(data.purchase_amount)) ? undefined : data.purchase_amount,
        age_months: (data.age_months === null || data.age_months === undefined) 
          ? undefined 
          : (typeof data.age_months === 'number' 
              ? (isNaN(data.age_months) ? undefined : data.age_months)
              : undefined),
        // Date of Birth (optional)
        dob: data.dob === '' || data.dob === null ? undefined : data.dob,
        // Sale information (conditional)
        sale_date: data.sale_date === '' || data.sale_date === null ? undefined : data.sale_date,
        sale_amount: (data.sale_amount === null || data.sale_amount === undefined || data.sale_amount === 0 || isNaN(data.sale_amount)) ? undefined : data.sale_amount,
        // Death information (conditional)
        dead_date: data.dead_date === '' || data.dead_date === null ? undefined : data.dead_date,
        reason_of_death: data.reason_of_death === '' || data.reason_of_death === null ? undefined : data.reason_of_death,
        // Latest weight
        latest_weight_kg: (data.latest_weight_kg === null || data.latest_weight_kg === undefined || data.latest_weight_kg === 0 || isNaN(data.latest_weight_kg)) ? undefined : data.latest_weight_kg,
        latest_weight_date: data.latest_weight_date === '' || data.latest_weight_date === null ? undefined : data.latest_weight_date,
        // Don't send birth_weight_kg (removed from UI)
        birth_weight_kg: undefined,
      };
      
      // Remove undefined values to clean up the payload
      Object.keys(goatData).forEach(key => {
        if (goatData[key] === undefined) {
          delete goatData[key];
        }
      });

      let goatId = id;
      
      if (id) {
        await updateGoat(`/goat/master/${id}`, goatData, 'PUT');
      } else {
        const createdGoat = await createGoat('/goat/master', goatData);
        if (!createdGoat?.id) {
          throw new Error('Failed to create goat: No ID returned');
        }
        goatId = createdGoat.id;
      }
      
      // Upload image if a new image was selected
      if (imagePreview && goatId) {
        try {
          setUploadingImage(true);
          await uploadGoatImage(goatId, imagePreview);
        } catch (err: any) {
          // Don't fail the whole form submission if image upload fails
          console.error('Image upload failed:', err);
          setError(getErrorMessage(err, 'Goat saved but image upload failed'));
          setUploadingImage(false);
          return; // Stay on page to show error
        } finally {
          setUploadingImage(false);
        }
      }
      
      navigate('/goat/master');
    } catch (err: any) {
      // Ensure error is always a string, not an object
      const errorMsg = getErrorMessage(err, 'Failed to save goat record');
      // Additional check: if errorMsg is still an object, stringify it
      if (typeof errorMsg !== 'string') {
        setError(JSON.stringify(errorMsg));
      } else {
        setError(errorMsg);
      }
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const availableParentTags = existingGoats
    .filter(g => g.tag_no !== form.watch('tag_no'))
    .map(g => g.tag_no);

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/master" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Edit' : 'Add'} Goat Record
          </h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Group 1: Tag Number, Category, Breed with Image */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left side: Form fields */}
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <label className="form-label">Tag Number *</label>
                  <input
                    type="text"
                    {...form.register('tag_no', {
                      onBlur: () => form.trigger('tag_no')
                    })}
                    className="form-input"
                    placeholder="e.g., G001"
                  />
                  {form.formState.errors.tag_no && (form.formState.isSubmitted || form.formState.touchedFields.tag_no) && (
                    <p className="mt-1 text-sm text-red-600">
                      {typeof form.formState.errors.tag_no.message === 'string' 
                        ? form.formState.errors.tag_no.message 
                        : 'Tag number is required'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">Category *</label>
                  <select {...form.register('category')} className="form-input">
                    <option value="doe">Doe</option>
                    <option value="buck">Buck</option>
                    <option value="kid">Kid</option>
                  </select>
                  {form.formState.errors.category && (form.formState.isSubmitted || form.formState.touchedFields.category) && (
                    <p className="mt-1 text-sm text-red-600">
                      {typeof form.formState.errors.category.message === 'string' 
                        ? form.formState.errors.category.message 
                        : 'Category is required'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">Breed</label>
                  <select {...form.register('breed')} className="form-input">
                    <option value="">Select Breed (Optional)</option>
                    {(goatBreeds || []).map((breed: any) => (
                      <option key={breed.id} value={breed.name}>{breed.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right side: Image */}
              <div className="lg:col-span-1">
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label mb-0">Goat Image (Optional)</label>
                  {/* Remove button - shown when image exists and user is admin */}
                  {(existingImage || imagePreview) && user?.role === 'admin' && (
                    <button
                      type="button"
                      onClick={() => {
                        if (existingImage && !imagePreview) {
                          removeExistingImage();
                        } else if (imagePreview) {
                          removeImage();
                        }
                      }}
                      disabled={uploadingImage}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50 btn-touch p-1"
                      title="Remove image"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <div className="flex justify-center lg:justify-start">
                  <div className="relative">
                    {/* Hidden file input */}
                    <input
                      id="goat-image-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    
                    {/* Display existing image if editing */}
                    {existingImage && !imagePreview && (
                      <div className="relative w-32 h-32">
                        <button
                          type="button"
                          onClick={() => openImageModal(existingImage)}
                          className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          <img
                            src={existingImage}
                            alt="Goat"
                            className="w-full h-full object-cover"
                          />
                        </button>
                        {/* Edit button - small, always visible */}
                        <label
                          htmlFor="goat-image-input"
                          className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 active:bg-green-700 btn-touch shadow-md cursor-pointer border-2 border-white z-10"
                          title="Change image"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PencilIcon className="h-3 w-3" />
                        </label>
                      </div>
                    )}

                    {/* Display preview of new image */}
                    {imagePreview && (
                      <div className="relative w-32 h-32">
                        <button
                          type="button"
                          onClick={() => openImageModal(imagePreview)}
                          className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          <img
                            src={imagePreview}
                            alt="Goat preview"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      </div>
                    )}

                    {/* Placeholder when no image */}
                    {!existingImage && !imagePreview && (
                      <label
                        htmlFor="goat-image-input"
                        className="w-32 h-32 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        <div className="text-center">
                          <CameraIcon className="mx-auto h-8 w-8 text-gray-400" />
                        </div>
                      </label>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center lg:text-left">
                  Click to upload (Max 10MB, compressed to 500KB)
                </p>
                <p className="mt-0.5 text-xs text-gray-400 text-center lg:text-left">
                  Mobile: Opens camera directly
                </p>
              </div>
            </div>
          </div>

          {/* Image Modal */}
          {showImageModal && modalImageSrc && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={closeImageModal}
            >
              <div 
                className="relative max-w-4xl max-h-[90vh] w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={closeImageModal}
                  className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 shadow-lg z-10 btn-touch"
                  title="Close"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-600" />
                </button>
                <img
                  src={modalImageSrc}
                  alt="Goat"
                  className="w-full h-auto rounded-lg shadow-2xl object-contain max-h-[90vh]"
                />
              </div>
            </div>
          )}

          {/* Group 2: Purchase Date and Age at Purchase */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Purchase Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Purchase Date{!id && ' *'}</label>
                <input
                  type="date"
                  {...form.register('purchase_date')}
                  className="form-input"
                  required={!id}
                />
                {form.formState.errors.purchase_date && (form.formState.isSubmitted || form.formState.touchedFields.purchase_date) && (
                  <p className="mt-1 text-sm text-red-600">
                    {typeof form.formState.errors.purchase_date.message === 'string' 
                      ? form.formState.errors.purchase_date.message 
                      : 'Purchase date is required'}
                  </p>
                )}
                {(form.formState.errors.root?.message?.includes('purchase-date-if-age') || form.formState.errors.purchase_date?.message?.includes('purchase-date-if-age')) && form.formState.isSubmitted && id && (
                  <p className="mt-1 text-sm text-red-600">
                    Purchase date is required when age at purchase is provided
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {!id 
                    ? 'Date when the goat was purchased' 
                    : 'Date when the goat was purchased (required if age is provided)'}
                </p>
              </div>

              <div>
                <label className="form-label">Estimated Age at Purchase (Months){!id && ' *'}</label>
                <input
                  type="number"
                  {...form.register('age_months', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return undefined;
                      const num = Number(value);
                      return isNaN(num) ? undefined : num;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., 0, 6, 12, 24"
                  min="0"
                  max="300"
                  required={!id}
                />
                {form.formState.errors.age_months && (form.formState.isSubmitted || form.formState.touchedFields.age_months) && (
                  <p className="mt-1 text-sm text-red-600">
                    {typeof form.formState.errors.age_months.message === 'string' 
                      ? form.formState.errors.age_months.message 
                      : 'Estimated age at purchase is required'}
                  </p>
                )}
                {(form.formState.errors.root?.message?.includes('purchase-date-if-age') || form.formState.errors.age_months?.message?.includes('purchase-date-if-age')) && form.formState.isSubmitted && id && (
                  <p className="mt-1 text-sm text-red-600">
                    Purchase date is required when age at purchase is provided
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {!id 
                    ? 'Enter the goat\'s age in months at the time of purchase'
                    : 'Enter the goat\'s age in months at the time of purchase (if provided, purchase date is required)'}
                </p>
              </div>

              <div>
                <label className="form-label">Purchase Amount (₹){!id && ' *'}</label>
                <input
                  type="number"
                  step="0.01"
                  {...form.register('purchase_amount', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return undefined;
                      const num = Number(value);
                      return isNaN(num) ? undefined : num;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., 5000.00"
                  min="0.01"
                  required={!id}
                />
                {form.formState.errors.purchase_amount && (form.formState.isSubmitted || form.formState.touchedFields.purchase_amount) && (
                  <p className="mt-1 text-sm text-red-600">
                    {typeof form.formState.errors.purchase_amount.message === 'string' 
                      ? form.formState.errors.purchase_amount.message 
                      : 'Invalid purchase amount'}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {!id 
                    ? 'Amount paid when the goat was purchased (required)' 
                    : 'Amount paid when the goat was purchased (optional)'}
                </p>
              </div>
            </div>
          </div>

          {/* Show remaining fields only in edit mode */}
          {id && (
            <>
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Purchase Information (Continued)
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      {...form.register('dob')}
                      className="form-input"
                      max={getTodayDate()}
                    />
                    <p className="mt-1 text-xs text-gray-500">Enter exact date of birth if known (for farm-born goats)</p>
                  </div>

                  <div>
                    <label className="form-label">Shed Assignment</label>
                    <select {...form.register('shed_id')} className="form-input">
                      <option value="">Select Shed (Optional)</option>
                      {sheds.map((shed: any) => (
                        <option key={shed.id} value={shed.id}>{shed.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Assign goat to a specific shed</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Additional Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Mother Tag</label>
                    <select {...form.register('mother_tag')} className="form-input">
                      <option value="">Select Mother (Optional)</option>
                      {availableParentTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Father Tag</label>
                    <select {...form.register('father_tag')} className="form-input">
                      <option value="">Select Father (Optional)</option>
                      {availableParentTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Status *</label>
                    <select {...form.register('status')} className="form-input">
                      <option value="active">Active</option>
                      <option value="sold">Sold</option>
                      <option value="dead">Dead</option>
                      <option value="transferred">Transferred</option>
                    </select>
                    {form.formState.errors.status && (form.formState.isSubmitted || form.formState.touchedFields.status) && (
                      <p className="mt-1 text-sm text-red-600">
                        {typeof form.formState.errors.status.message === 'string' 
                          ? form.formState.errors.status.message 
                          : 'Status is required'}
                      </p>
                    )}
                  </div>

                  {/* Is Breeder checkbox - only show for bucks */}
                  {form.watch('category') === 'buck' && (
                    <div className="sm:col-span-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          {...form.register('is_breeder')}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Mark as Breeder</span>
                        <span className="text-xs text-gray-500">(Only breeder bucks will appear in breeding forms)</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Sale Information - Conditional on Status */}
              {form.watch('status') === 'sold' && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Sale Information
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Sale Date</label>
                      <input
                        type="date"
                        {...form.register('sale_date')}
                        className="form-input"
                        max={getTodayDate()}
                      />
                      <p className="mt-1 text-xs text-gray-500">Date when the goat was sold</p>
                    </div>

                    <div>
                      <label className="form-label">Sale Amount (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        {...form.register('sale_amount', {
                          setValueAs: (value) => {
                            if (value === '' || value === null || value === undefined) return undefined;
                            const num = Number(value);
                            return isNaN(num) ? undefined : num;
                          }
                        })}
                        className="form-input"
                        placeholder="e.g., 5000.00"
                        min="0"
                      />
                      {form.formState.errors.sale_amount && (
                        <p className="mt-1 text-sm text-red-600">
                          {typeof form.formState.errors.sale_amount.message === 'string' 
                            ? form.formState.errors.sale_amount.message 
                            : 'Invalid sale amount'}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">Amount received from sale</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Death Information - Conditional on Status */}
              {form.watch('status') === 'dead' && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Death Information
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Death Date</label>
                      <input
                        type="date"
                        {...form.register('dead_date')}
                        className="form-input"
                        max={getTodayDate()}
                      />
                      <p className="mt-1 text-xs text-gray-500">Date when the goat died</p>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="form-label">Reason of Death</label>
                      <textarea
                        {...form.register('reason_of_death')}
                        className="form-input"
                        rows={3}
                        placeholder="Enter reason of death (e.g., Disease, Accident, Old age, etc.)"
                      />
                      <p className="mt-1 text-xs text-gray-500">Provide details about the cause of death</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Latest Weight */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Weight Tracking
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Latest Weight (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      {...form.register('latest_weight_kg', {
                        setValueAs: (value) => {
                          if (value === '' || value === null || value === undefined) return undefined;
                          const num = Number(value);
                          return isNaN(num) ? undefined : num;
                        }
                      })}
                      className="form-input"
                      placeholder="e.g., 25.5"
                      min="0"
                    />
                    {form.formState.errors.latest_weight_kg && (
                      <p className="mt-1 text-sm text-red-600">
                        {typeof form.formState.errors.latest_weight_kg.message === 'string' 
                          ? form.formState.errors.latest_weight_kg.message 
                          : 'Invalid weight'}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Most recent weight measurement</p>
                  </div>

                  <div>
                    <label className="form-label">Weight Date</label>
                    <input
                      type="date"
                      {...form.register('latest_weight_date')}
                      className="form-input"
                      max={getTodayDate()}
                    />
                    <p className="mt-1 text-xs text-gray-500">Date when latest weight was recorded</p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={creating || updating || uploadingImage}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {(creating || updating || uploadingImage) ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">{uploadingImage ? 'Uploading image...' : (id ? 'Updating...' : 'Creating...')}</span>
                </>
              ) : (
                (id ? 'Update' : 'Create')
              )}
            </button>
            <Link
              to="/goat/master"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatMasterForm;
